/**
 * instagram-feed.js — play a reel on hover, stop it on the way out.
 *
 * The videos ship with `preload="none"`, so nothing is fetched until a pointer
 * actually arrives: six reels cost six poster images on page load, not six
 * videos. The first hover is what triggers the download.
 *
 * Deliberately pointer-only. On touch there is no hover, and autoplaying six
 * videos in a scrolling row would burn a phone's data and battery for a section
 * nobody asked to watch — a tap follows the link to Instagram instead, which is
 * what the card is for.
 */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bind(link) {
    var video = link.querySelector('video');
    if (!video) return;

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');

    var playing = false;

    function start() {
      if (playing) return;
      playing = true;
      link.classList.add('is-playing');
      var p = video.play();
      // Autoplay can still be refused (power saving, strict settings). Falling
      // back to the poster is fine — never surface an error to the shopper.
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          playing = false;
          link.classList.remove('is-playing');
        });
      }
    }

    function stop() {
      if (!playing) return;
      playing = false;
      link.classList.remove('is-playing');
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore — seeking before metadata loads throws in some browsers */
      }
    }

    // `mouse` only: a pen or touch pointer firing pointerenter would start
    // playback the user never asked for.
    link.addEventListener('pointerenter', function (event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      start();
    });
    link.addEventListener('pointerleave', stop);

    // Keyboard users get the same behaviour as hover.
    link.addEventListener('focus', start);
    link.addEventListener('blur', stop);

    // Scrolling a card out of view while it plays wastes decode work.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) stop();
          });
        },
        { threshold: 0 }
      ).observe(link);
    }
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-ig-video]').forEach(bind);
  }

  if (reduced) return; // honour the same preference the CSS does

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot();
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
